import React from 'react';
import { Field, FieldArray, FieldArrayRenderProps } from 'formik';
import { Box, Flex, FlexProps, Text } from 'pounceV2';
import { CompoundInputProps as CompoundInputPropsInterface } from 'Components/CompoundInput/CompoundInputV3';
import { safeguardErrorMsg } from 'Components/fields/utils';
import SimpleSelectChipFieldV3 from 'Components/forms/BaseDetectionForm/CompoundInputFieldArray/SimpleSelectChipFieldV3';
import { useTheme } from '@mui/material';
import CompoundInputFieldArrayItemV3 from './ItemV3';
import AddMenuButtonV3 from './AddMenuButtonV3';

export type CompoundInputFieldArrayMetaProps = {
  namePrefix: string;
  index: number;
};

/**
 * Data expected in the format [{conjunction: 'and', values: [{conjunction: 'or', values: []}]}]}
 */
type CompoundInputFieldArrayProps<V> = {
  name: string;
  children: (item: V, meta: CompoundInputFieldArrayMetaProps) => React.ReactNode;
  validateOnChange?: boolean;
  newItem?: Record<string, any>;
  allowGroups?: boolean;
  maximumNestingDepth?: number;
  unitLabel?: string;
  compoundInputProps?: Partial<CompoundInputPropsInterface>;
  disabled?: boolean;
  // Word preceeding the logic chips
  preface?: string;
  showJoinChips?: boolean;
} & Partial<Omit<FlexProps, 'children'>>;

function CompoundInputFieldArrayWithJoinsV3<V>(
  props: CompoundInputFieldArrayProps<V>,
): React.ReactElement {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  const {
    name,
    children,
    validateOnChange = false,
    newItem = {},
    allowGroups = false,
    maximumNestingDepth,
    unitLabel,
    compoundInputProps = {},
    disabled = false,
    preface = '',
    ...rest
  } = props;

  return (
    <FieldArray name={`${name}.values`} validateOnChange={validateOnChange}>
      {({ push, remove, form }: FieldArrayRenderProps) => {
        const { value = { conjunction: 'and', values: [] } } = form.getFieldProps(`${name}`);
        const arrayVals =
          value?.values?.filter(item => {
            return !item?.conjunction || item.values.length !== 0;
          }) ?? [];

        return (
          <Flex key={name} align="center" wrap="wrap" gap={2} {...rest}>
            {preface && <Text>{preface}</Text>}
            {arrayVals.map((item, index: number) => {
              return (
                <Box key={`${name}[${index}]-field`}>
                  {allowGroups ? (
                    <Flex gap={2} align={'center'}>
                      {/* Don't show the AND/OR chip before the first logical statement */}
                      {index === 1 && (
                        // The chip between the first and second logical statements should be a dropdown

                        <Field
                          as={SimpleSelectChipFieldV3}
                          name={`${name}.conjunction`}
                          items={['and', 'or']}
                          disabled={disabled}
                        />
                      )}
                      {index > 1 && (
                        // All following AND/OR connectors on the same level should be words, to avoid a logically vague statement like "x AND y OR z"
                        <Box minWidth={40} textAlign="center">
                          {value.conjunction}
                        </Box>
                      )}
                      {item?.conjunction ? (
                        <CompoundInputFieldArrayWithJoinsV3
                          {...props}
                          maximumNestingDepth={
                            typeof maximumNestingDepth === 'number'
                              ? maximumNestingDepth - 1
                              : undefined
                          }
                          name={`${name}.values[${index}]`}
                          disabled={disabled}
                          borderRadius="6px"
                          px={2}
                          py={1}
                          border="1px solid"
                          borderColor={tokens.stroke.neutral[5]}
                          preface=""
                        >
                          {children}
                        </CompoundInputFieldArrayWithJoinsV3>
                      ) : (
                        <CompoundInputFieldArrayItemV3
                          disabled={disabled}
                          backgroundColor={componentTokens.tags.colors.condition.background.resting}
                          key={`CompoundInputFieldArray-item-${name}-${index}`}
                          error={
                            form.getFieldMeta(`${name}.values[${index}]`)?.error
                              ? safeguardErrorMsg(
                                  form.getFieldMeta(`${name}.values[${index}]`)?.error,
                                )
                              : undefined
                          }
                          onRemove={() => remove(index)}
                          {...compoundInputProps}
                        >
                          {children(item as V, { namePrefix: `${name}.values[${index}]`, index })}
                        </CompoundInputFieldArrayItemV3>
                      )}
                    </Flex>
                  ) : (
                    <CompoundInputFieldArrayItemV3
                      disabled={disabled}
                      backgroundColor={componentTokens.tags.colors.condition.background.resting}
                      key={`CompoundInputFieldArray-item-${name}-${index}`}
                      error={
                        form.getFieldMeta(`${name}.values[${index}]`)?.error
                          ? safeguardErrorMsg(form.getFieldMeta(`${name}.values[${index}]`)?.error)
                          : undefined
                      }
                      onRemove={() => remove(index)}
                      {...compoundInputProps}
                    >
                      {children(item as V, { namePrefix: `${name}.values[${index}]`, index })}
                    </CompoundInputFieldArrayItemV3>
                  )}
                </Box>
              );
            })}
            <AddMenuButtonV3
              push={push}
              newItem={newItem}
              disabled={disabled}
              newGroup={{ conjunction: 'and', values: [newItem] }}
              unitLabel={unitLabel}
              allowGroups={
                allowGroups && (typeof maximumNestingDepth !== 'number' || maximumNestingDepth > 0)
              }
            />
          </Flex>
        );
      }}
    </FieldArray>
  );
}
export default CompoundInputFieldArrayWithJoinsV3;
